/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api/authorizationCodesApi.ts (Authorization Codes)
 🗓️ Created: 2025-01-15
 🧩 Description:
    API service layer for Authorization Codes.
    Centralizes all API calls for better maintainability.

────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import {
  ListAuthorizationCodesParams,
  ListAuthorizationCodesResponse,
  AuthorizationCode,
  ImportResults
} from '../types';

/**
 * Fetch authorization codes list
 */
export async function fetchAuthorizationCodes(
  params: ListAuthorizationCodesParams,
  tenantSlug: string
): Promise<ListAuthorizationCodesResponse> {
  const queryParams = new URLSearchParams();
  
  if (params.page) queryParams.set('page', params.page.toString());
  if (params.limit) queryParams.set('limit', params.limit.toString());
  if (params.sortBy) queryParams.set('sortBy', params.sortBy);
  if (params.sortOrder) queryParams.set('sortOrder', params.sortOrder);
  if (params.search) queryParams.set('search', params.search);
  if (params.domainId) queryParams.set('domainId', params.domainId.toString());

  const response = await fetch(
    buildApiUrl(`/api/telephony/authorization-codes?${queryParams.toString()}`),
    { headers: getApiHeaders(tenantSlug) }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch authorization codes');
  }

  const result = await response.json();
  if (!result?.success) {
    throw new Error(result?.message || 'Failed to fetch authorization codes');
  }

  return result;
}

/**
 * Fetch single authorization code by ID
 */
export async function fetchAuthorizationCodeById(
  id: string | number,
  tenantSlug: string
): Promise<AuthorizationCode> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/authorization-codes/${id}`),
    { headers: getApiHeaders(tenantSlug) }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch authorization code');
  }

  const result = await response.json();
  if (!result?.success) {
    throw new Error(result?.message || 'Failed to fetch authorization code');
  }

  return result.data;
}

/**
 * Create authorization code
 */
export async function createAuthorizationCode(
  payload: {
    domainId: number;
    code: string;
    alias?: string | null;
    description?: string | null;
    cosId: number;
    active?: boolean;
  },
  tenantSlug: string
): Promise<AuthorizationCode> {
  const response = await fetch(
    buildApiUrl('/api/telephony/authorization-codes'),
    {
      method: 'POST',
      headers: getApiHeaders(tenantSlug),
      body: JSON.stringify(payload)
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.message || 'Failed to create authorization code');
  }

  const result = await response.json();
  if (!result?.success) {
    throw new Error(result?.message || 'Failed to create authorization code');
  }

  return result.data || { ...payload, id: result.data?.id, uuid: result.data?.uuid };
}

/**
 * Update authorization code
 */
export async function updateAuthorizationCode(
  id: string | number,
  payload: {
    code?: string;
    alias?: string | null;
    description?: string | null;
    cosId?: number;
    active?: boolean;
  },
  tenantSlug: string
): Promise<AuthorizationCode> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/authorization-codes/${id}`),
    {
      method: 'PUT',
      headers: getApiHeaders(tenantSlug),
      body: JSON.stringify(payload)
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.message || 'Failed to update authorization code');
  }

  const result = await response.json();
  if (!result?.success) {
    throw new Error(result?.message || 'Failed to update authorization code');
  }

  return result.data || { ...payload, id: Number(id), uuid: result.data?.uuid };
}

/**
 * Delete authorization code by ID
 */
export async function deleteAuthorizationCodeById(
  id: string | number,
  tenantSlug: string
): Promise<void> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/authorization-codes/${id}`),
    {
      method: 'DELETE',
      headers: getApiHeaders(tenantSlug)
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.message || 'Failed to delete authorization code');
  }
}

/**
 * Export authorization codes to CSV
 */
export async function exportAuthorizationCodes(
  domainId: number,
  tenantSlug: string
): Promise<Blob> {
  const queryParams = new URLSearchParams({
    domainId: domainId.toString()
  });

  const response = await fetch(
    buildApiUrl(`/api/telephony/authorization-codes/export?${queryParams.toString()}`),
    { headers: getApiHeaders(tenantSlug) }
  );

  if (!response.ok) {
    throw new Error('Failed to export authorization codes');
  }

  const csvContent = await response.text();
  return new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
}

/**
 * Import authorization codes from CSV
 */
export async function importAuthorizationCodes(
  csvContent: string,
  mode: 'skip' | 'update' | 'replace',
  domainId: number,
  tenantSlug: string
): Promise<ImportResults> {
  const response = await fetch(
    buildApiUrl('/api/telephony/authorization-codes/import'),
    {
      method: 'POST',
      headers: getApiHeaders(tenantSlug),
      body: JSON.stringify({
        csvContent,
        mode,
        domainId
      })
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.message || 'Failed to import authorization codes');
  }

  const result = await response.json();
  if (!result?.success) {
    throw new Error(result?.message || 'Failed to import authorization codes');
  }

  return result.data;
}

